import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps, type MouseEvent, useEffect, useRef, useState } from 'react';
import { armClick, type ClickOutcome } from '../../lib/click-feedback';
import { cn } from '../../lib/cn';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline: 'border border-input bg-surface hover:bg-muted',
        ghost: 'hover:bg-muted',
        danger: 'bg-danger text-white hover:bg-danger/90',
      },
      size: { sm: 'h-8 px-3', md: 'h-9 px-4', icon: 'size-9' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

/** busy: the click's requests are running · done / failed: they settled · local: the click only changed the page. */
type FeedbackState = 'idle' | 'busy' | ClickOutcome;

/** How long the done / failed colour stays before the button goes back to normal (or stays disabled). */
export const FEEDBACK_HOLD_MS = { done: 1200, failed: 1800, local: 300 } as const;

// Done and failed use the state colours' text tones: they keep 4.5:1 against the surface colour in both themes.
const RESULT: Record<'done' | 'failed', string> = {
  done: 'border-transparent bg-success-foreground text-surface hover:bg-success-foreground',
  failed: 'border-transparent bg-danger-foreground text-surface hover:bg-danger-foreground',
};

const BAR: Record<FeedbackState, string> = {
  idle: 'w-0 opacity-0',
  busy: 'animate-button-progress motion-reduce:w-full motion-reduce:animate-none',
  done: 'w-full opacity-0 transition-[width,opacity] duration-200',
  failed: 'w-full opacity-0 transition-[width,opacity] duration-200',
  local: 'w-full opacity-0 transition-[width,opacity] duration-300 motion-reduce:transition-none',
};

/**
 * Every click shows that it landed: a bar fills the button from the start edge while the requests the click started
 * run (lib/click-feedback.ts), then the button turns green when they succeeded or red when one failed, and goes back
 * to normal. A click that only changed the page gets a quick fill and nothing else. `feedback={false}` opts out.
 *
 * `context` adds words for screen readers after the visible label ("Check now" + "for Ministry on X"), so a row's
 * button says which row it acts on while its name still starts with the words on screen (WCAG 2.5.3 Label in Name).
 * Use it instead of an `aria-label` that rewords the visible text.
 */
export function Button({
  className,
  variant,
  size,
  asChild = false,
  feedback = true,
  context,
  onClick,
  children,
  ...props
}: ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean; feedback?: boolean; context?: string }) {
  // The space sits outside the span: a name is computed per element, and text at the edge of one is trimmed.
  const extra = context ? (
    <>
      {' '}
      <span className="sr-only">{context}</span>
    </>
  ) : null;
  const [state, setState] = useState<FeedbackState>('idle');
  const drop = useRef<(() => void) | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      drop.current?.();
      if (hold.current) clearTimeout(hold.current);
    },
    [],
  );

  // A link styled as a button (asChild) navigates away: Slot takes one child, so it gets no bar.
  if (asChild || !feedback) {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} onClick={onClick} {...props}>
        {asChild ? (
          children
        ) : (
          <>
            {children}
            {extra}
          </>
        )}
      </Comp>
    );
  }

  const click = (e: MouseEvent<HTMLButtonElement>) => {
    drop.current?.();
    if (hold.current) clearTimeout(hold.current);
    setState('busy');
    // Armed before the handler runs, so a request it starts synchronously belongs to this click.
    drop.current = armClick(outcome => {
      drop.current = null;
      setState(outcome);
      hold.current = setTimeout(() => setState('idle'), FEEDBACK_HOLD_MS[outcome]);
    });
    onClick?.(e);
  };

  return (
    <button
      className={cn(
        buttonVariants({ variant, size }),
        'relative isolate overflow-hidden',
        className,
        (state === 'done' || state === 'failed') && RESULT[state],
      )}
      onClick={click}
      aria-busy={state === 'busy' || undefined}
      data-feedback={state === 'idle' ? undefined : state}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute start-0 -z-10 bg-current',
          // White text on the danger red has little contrast to spare: there the bar is a strip along the bottom.
          variant === 'danger' ? 'bottom-0 h-1' : 'inset-y-0',
          state === 'busy' && (variant === 'danger' ? 'opacity-70' : 'opacity-20 motion-reduce:opacity-10'),
          BAR[state],
        )}
      />
      {children}
      {extra}
    </button>
  );
}
